import {DeleteOutlined, LeftOutlined, RightOutlined, StarFilled, UploadOutlined} from "@ant-design/icons";
import {editVehicleThunk, setCurrentVehicleAsync, useAppDispatch, useAppSelector} from "@/store";
import {useEffect, useMemo, useRef, useState} from "react";
import {Button, Card, Divider, Empty, Image, Progress, Segmented, Tag, Typography} from "antd";
import {useNavigate, useParams} from "react-router";
import {getStatusColor} from "@/components/common.tsx";
import LoadingScreen from "@/components/navigation/loadingScreen.tsx";
import type {CarAuction, CarItem} from "@/types";
import {calculateScore, getLevel, getMissing, toMoneyFormat} from "@/utils";
import {usePostHog} from '@posthog/react';

const {Title, Text,Paragraph} = Typography;

type EditableVehicle = CarItem | CarAuction;

function parseNumber(value: string, fallback: number) {
    const parsed = Number(value.replace(/[^\d.-]/g, ""));
    return Number.isFinite(parsed) ? parsed : fallback;
}

function parseDateString(value: string, fallback: string) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? fallback : parsed.toISOString();
}

function EditableSpecRow({
    label,
    value,
    placeholder,
    onChange
}: {
    label: string;
    value: string | number | undefined;
    placeholder: string;
    onChange: (nextValue: string) => void;
}) {
    const isEmpty = value === undefined || value === null || value === "";
    const displayValue = isEmpty ? placeholder : String(value);

    return (
        <div className="flex items-center justify-between gap-3">
            <Text type="secondary">{label}</Text>
            <Text
                editable={{onChange}}
                className={`text-right font-medium ${isEmpty ? "text-zinc-400" : ""}`}
            >
                {displayValue}
            </Text>
        </div>
    );
}

function EditableDescriptionBlock({
    label,
    value,
    placeholder,
    onChange
}: {
    label: string;
    value: string | undefined;
    placeholder: string;
    onChange: (nextValue: string) => void;
}) {
    const isEmpty = !value;

    return (
        <div className="space-y-1">
            <Text type="secondary">{label}</Text>
            <Paragraph
                editable={{onChange}}
                className={`mb-0! p-3 ${isEmpty ? "text-zinc-400" : ""}`}
            >
                {value || placeholder}
            </Paragraph>
        </div>
    );
}

export default function EditVehicleScreen() {
    const dispatch = useAppDispatch();
    const {currentVehicle, loading} = useAppSelector((state) => state.vehicle);
    const {id} = useParams();
    const navigate = useNavigate();
    const [draftVehicle, setDraftVehicle] = useState<EditableVehicle | null>(null);
    const posthog = usePostHog();

    useEffect(() => {
        if (!id) return;
        if (currentVehicle?.id === id) return;
        dispatch(setCurrentVehicleAsync(id));
    }, [dispatch, id, currentVehicle?.id]);

    useEffect(() => {
        if (!currentVehicle) return;
        setDraftVehicle(currentVehicle);
    }, [currentVehicle]);

    const updateField = <K extends keyof EditableVehicle>(field: K, value: EditableVehicle[K]) => {
        setDraftVehicle((previous) => previous ? ({...previous, [field]: value}) : previous);
    };

    const updateAuctionField = <K extends keyof CarAuction>(field: K, value: CarAuction[K]) => {
        setDraftVehicle((previous) => previous?.type === "auction" ? ({...previous, [field]: value}) : previous);
    };

    const updateListingField = <K extends keyof CarItem>(field: K, value: CarItem[K]) => {
        setDraftVehicle((previous) => previous?.type === "listing" ? ({...previous, [field]: value}) : previous);
    };

    const updateImages = (images: string[]) => {
        setDraftVehicle((previous) => previous ? ({...previous, images}) : previous);
    };

    const updateDescriptionField = <K extends keyof EditableVehicle["description"]>(
        field: K,
        value: EditableVehicle["description"][K]
    ) => {
        setDraftVehicle((previous) => previous
            ? {
                ...previous,
                description: {
                    ...previous.description,
                    [field]: value
                }
            }
            : previous);
    };

    const handleSave = async () => {
        if (!draftVehicle) return;
        try {
            await dispatch(editVehicleThunk(draftVehicle)).unwrap();
            posthog.capture('vehicle_edited', {
                vehicle_id: draftVehicle.id,
                type: draftVehicle.type,
                brand: draftVehicle.brand,
                model: draftVehicle.model,
                year: draftVehicle.year,
            });
        } catch (error) {
            posthog.captureException(error instanceof Error ? error : new Error("Failed to save vehicle"));
            throw error;
        }
    };

    if (loading && !draftVehicle) return <LoadingScreen/>;
    if (!draftVehicle) return <Empty description={"No Vehicle found"}/>;

    return (
        <div className="px-3 py-2 sm:px-4 md:px-6 lg:px-8">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <Title level={3} className="mb-1! flex flex-wrap items-baseline gap-2">
                        <Text editable={{onChange: (value) => updateField("year", parseNumber(value, draftVehicle.year))}}>
                            {draftVehicle.year}
                        </Text>
                        <Text editable={{onChange: (value) => updateField("brand", value)}}>
                            {draftVehicle.brand}
                        </Text>
                        <Text editable={{onChange: (value) => updateField("model", value)}}>
                            {draftVehicle.model}
                        </Text>
                    </Title>
                    <div className="flex flex-wrap items-center gap-2">
                        <Text type="secondary">VIN</Text>
                        <Text editable={{onChange: (value) => updateField("vin", value)}}>
                            {draftVehicle.vin ?? "Add VIN"}
                        </Text>
                        <Text type="secondary">|</Text>
                        <Text editable={{onChange: (value) => updateField("titleStatus", value)}}>
                            {draftVehicle.titleStatus || "Add title status"}
                        </Text>
                        <Text type="secondary">title</Text>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                        <Tag color={getStatusColor(draftVehicle.active)}>
                            Status: {draftVehicle.active ?? "Unknown"}
                        </Tag>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button onClick={() => navigate(-1)}>Back to Vehicle</Button>
                    <Button type="primary" loading={loading} onClick={handleSave}>
                        Save
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div className="lg:col-span-2">
                    <EditVehicleGallery
                        images={draftVehicle.images}
                        title={`${draftVehicle.year} ${draftVehicle.brand} ${draftVehicle.model}`}
                        onChange={updateImages}
                    />
                    <Divider/>
                    <div>
                        <Title level={5}>Description</Title>
                        <div className="space-y-3">
                            <EditableDescriptionBlock
                                label="General"
                                value={draftVehicle.description.general}
                                placeholder="Add a general vehicle overview"
                                onChange={(value) => updateDescriptionField("general", value)}
                            />
                            <EditableDescriptionBlock
                                label="Highlights"
                                value={draftVehicle.description.highlights}
                                placeholder="Add key highlights"
                                onChange={(value) => updateDescriptionField("highlights", value)}
                            />
                            <EditableDescriptionBlock
                                label="Equipment"
                                value={draftVehicle.description.equipment}
                                placeholder="Add equipment details"
                                onChange={(value) => updateDescriptionField("equipment", value)}
                            />
                            <EditableDescriptionBlock
                                label="Modifications"
                                value={draftVehicle.description.modifications}
                                placeholder="Add modifications"
                                onChange={(value) => updateDescriptionField("modifications", value)}
                            />
                            <EditableDescriptionBlock
                                label="Known Flaws"
                                value={draftVehicle.description.knownFlaws}
                                placeholder="Add known flaws"
                                onChange={(value) => updateDescriptionField("knownFlaws", value)}
                            />
                            <EditableDescriptionBlock
                                label="Service History"
                                value={draftVehicle.description.serviceHistory}
                                placeholder="Add service history"
                                onChange={(value) => updateDescriptionField("serviceHistory", value)}
                            />
                            <EditableDescriptionBlock
                                label="Ownership History"
                                value={draftVehicle.description.ownershipHistory}
                                placeholder="Add ownership history"
                                onChange={(value) => updateDescriptionField("ownershipHistory", value)}
                            />
                            <EditableDescriptionBlock
                                label="Seller Notes"
                                value={draftVehicle.description.sellerNotes}
                                placeholder="Add seller notes"
                                onChange={(value) => updateDescriptionField("sellerNotes", value)}
                            />
                        </div>
                    </div>
                </div>
                <div className="space-y-6">
                    <EditableVehicleCard
                        draftVehicle={draftVehicle}
                        updateField={updateField}
                        updateAuctionField={updateAuctionField}
                        updateListingField={updateListingField}
                    />
                </div>
            </div>


        </div>
    );
}

function EditableVehicleCard({
    draftVehicle,
    updateField,
    updateAuctionField,
    updateListingField
}: {
    draftVehicle: EditableVehicle;
    updateField: <K extends keyof EditableVehicle>(field: K, value: EditableVehicle[K]) => void;
    updateAuctionField: <K extends keyof CarAuction>(field: K, value: CarAuction[K]) => void;
    updateListingField: <K extends keyof CarItem>(field: K, value: CarItem[K]) => void;
}) {
    const healthScore = calculateScore(draftVehicle);
    const healthLevel = getLevel(healthScore);
    const missing = getMissing(draftVehicle).slice(0, 3);

    return (
        <Card className="h-fit rounded-xl">
            <div className="space-y-4">
                <Text type="secondary">Created: {new Date(draftVehicle.createdAt).toLocaleDateString()}</Text>

                <div className="flex items-center justify-between gap-3">
                    <Text type="secondary">Format</Text>
                    <Tag>{draftVehicle.type === "auction" ? "Auction" : "Fixed Price"}</Tag>
                </div>

                {draftVehicle.type === "auction" ? (
                    <>
                        <EditableSpecRow
                            label="Starting Bid"
                            value={`KSH ${toMoneyFormat(draftVehicle.startingBid)}`}
                            placeholder="Add starting bid"
                            onChange={(value) => updateAuctionField("startingBid", parseNumber(value, draftVehicle.startingBid))}
                        />
                        <EditableSpecRow
                            label="Reserve Price"
                            value={draftVehicle.reservePrice !== undefined ? `KSH ${toMoneyFormat(draftVehicle.reservePrice ?? 0)}` : undefined}
                            placeholder="Add reserve price"
                            onChange={(value) => {
                                const nextReservePrice = parseNumber(value, draftVehicle.reservePrice ?? 0);
                                updateAuctionField("reservePrice", nextReservePrice || undefined);
                            }}
                        />
                        <EditableSpecRow
                            label="Ending"
                            value={draftVehicle.ending}
                            placeholder="Add ending date"
                            onChange={(value) => updateAuctionField("ending", parseDateString(value, draftVehicle.ending))}
                        />
                    </>
                ) : (
                    <>
                        <EditableSpecRow
                            label="Price"
                            value={`KSH ${toMoneyFormat(draftVehicle.price)}`}
                            placeholder="Add price"
                            onChange={(value) => updateListingField("price", parseNumber(value, draftVehicle.price))}
                        />
                        <div className="flex items-center justify-between gap-3">
                            <Text type="secondary">Negotiation</Text>
                            <Segmented
                                value={draftVehicle.negotiable ?? false}
                                options={[
                                    {label: "Negotiable", value: true},
                                    {label: "Fixed", value: false}
                                ]}
                                onChange={(value) => updateListingField("negotiable", Boolean(value))}
                            />
                        </div>
                    </>
                )}

                <Divider className="my-3!"/>

                <EditableSpecRow
                    label="Mileage"
                    value={`${draftVehicle.mileage.toLocaleString()} KM`}
                    placeholder="Add mileage"
                    onChange={(value) => updateField("mileage", parseNumber(value, draftVehicle.mileage))}
                />
                <EditableSpecRow
                    label="Transmission"
                    value={draftVehicle.transmission}
                    placeholder="Add transmission"
                    onChange={(value) => updateField("transmission", value as EditableVehicle["transmission"])}
                />
                <EditableSpecRow
                    label="Fuel Type"
                    value={draftVehicle.fuelType}
                    placeholder="Add fuel type"
                    onChange={(value) => updateField("fuelType", value as EditableVehicle["fuelType"])}
                />
                <EditableSpecRow
                    label="Body"
                    value={draftVehicle.body}
                    placeholder="Add body type"
                    onChange={(value) => updateField("body", value)}
                />
                <EditableSpecRow
                    label="Color"
                    value={draftVehicle.color}
                    placeholder="Add exterior color"
                    onChange={(value) => updateField("color", value)}
                />

                <Divider className="my-3!"/>
                <Text strong>Optional Details</Text>

                <EditableSpecRow
                    label="Engine"
                    value={draftVehicle.engine}
                    placeholder="Add engine"
                    onChange={(value) => updateField("engine", value)}
                />
                <EditableSpecRow
                    label="Capacity"
                    value={draftVehicle.capacity}
                    placeholder="Add engine capacity"
                    onChange={(value) => updateField("capacity", value)}
                />
                <EditableSpecRow
                    label="Drivetrain"
                    value={draftVehicle.drivetrain}
                    placeholder="Add drivetrain"
                    onChange={(value) => updateField("drivetrain", value)}
                />
                <EditableSpecRow
                    label="Condition"
                    value={draftVehicle.condition}
                    placeholder="Add condition"
                    onChange={(value) => updateField("condition", value as EditableVehicle["condition"])}
                />
                <EditableSpecRow
                    label="Owners"
                    value={draftVehicle.owners}
                    placeholder="Add owner count"
                    onChange={(value) => updateField("owners", parseNumber(value, draftVehicle.owners ?? 0))}
                />
                <EditableSpecRow
                    label="Interior Material"
                    value={draftVehicle.interior}
                    placeholder="Add interior material"
                    onChange={(value) => updateField("interior", value)}
                />
                <EditableSpecRow
                    label="Interior Color"
                    value={draftVehicle.interiorColor}
                    placeholder="Add interior color"
                    onChange={(value) => updateField("interiorColor", value)}
                />
                <EditableSpecRow
                    label="Doors"
                    value={draftVehicle.doors}
                    placeholder="Add door count"
                    onChange={(value) => updateField("doors", parseNumber(value, draftVehicle.doors ?? 0))}
                />
                <EditableSpecRow
                    label="Seats"
                    value={draftVehicle.seats}
                    placeholder="Add seat count"
                    onChange={(value) => updateField("seats", parseNumber(value, draftVehicle.seats ?? 0))}
                />
                <EditableSpecRow
                    label="Horsepower"
                    value={draftVehicle.horsepower}
                    placeholder="Add horsepower"
                    onChange={(value) => updateField("horsepower", parseNumber(value, draftVehicle.horsepower ?? 0))}
                />
                <EditableSpecRow
                    label="Torque"
                    value={draftVehicle.torque}
                    placeholder="Add torque"
                    onChange={(value) => updateField("torque", parseNumber(value, draftVehicle.torque ?? 0))}
                />
            </div>

            <Divider/>

            <div className="flex items-center justify-between gap-3">
                <Text type="secondary">Listing quality score</Text>
                <Tag color={healthScore >= 90 ? "success" : healthScore >= 70 ? "processing" : healthScore >= 40 ? "warning" : "error"}>
                    {healthLevel}
                </Tag>
            </div>
            <Progress percent={healthScore} strokeColor="#13c2c2" showInfo/>
            {missing.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-2">
                    {missing.map((item) => (
                        <Tag key={item}>{item}</Tag>
                    ))}
                </div>
            ) : null}
        </Card>
    );
}

interface ListingGalleryProps {
    images: string[];
    title: string;
    onChange: (images: string[]) => void;
}

export function EditVehicleGallery({images, title, onChange}: ListingGalleryProps) {
    const objectUrlsRef = useRef<string[]>([]);
    const safeImages = useMemo(
        () => (images.length > 0 ? images : ["/placeholder.jpg"]),
        [images]
    );
    const [activeImage, setActiveImage] = useState(safeImages[0]);

    useEffect(() => {
        if (!safeImages.includes(activeImage)) {
            setActiveImage(safeImages[0]);
        }
    }, [activeImage, safeImages]);

    useEffect(() => {
        return () => {
            objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
            objectUrlsRef.current = [];
        };
    }, []);

    const syncImages = (nextImages: string[]) => {
        onChange(nextImages);
        setActiveImage(nextImages[0] ?? "/placeholder.jpg");
    };

    const addImages = (files: FileList | null) => {
        if (!files) return;

        const nextImages = Array.from(files).map((file) => {
            const url = URL.createObjectURL(file);
            objectUrlsRef.current.push(url);
            return url;
        });

        syncImages([...images, ...nextImages]);
    };

    const setCoverImage = (index: number) => {
        const nextImages = [...images];
        const [coverImage] = nextImages.splice(index, 1);
        if (!coverImage) return;
        nextImages.unshift(coverImage);
        syncImages(nextImages);
    };

    const moveImage = (index: number, direction: -1 | 1) => {
        const nextIndex = index + direction;
        if (nextIndex < 0 || nextIndex >= images.length) {
            return;
        }

        const nextImages = [...images];
        [nextImages[index], nextImages[nextIndex]] = [nextImages[nextIndex], nextImages[index]];
        syncImages(nextImages);
    };

    const removeImage = (index: number) => {
        const imageToRemove = images[index];
        if (imageToRemove?.startsWith("blob:")) {
            URL.revokeObjectURL(imageToRemove);
            objectUrlsRef.current = objectUrlsRef.current.filter((url) => url !== imageToRemove);
        }

        syncImages(images.filter((_, currentIndex) => currentIndex !== index));
    };

    return (
        <div>
            <div className="mb-2 flex items-center justify-between">
                <Text type="secondary">Media Manager</Text>
                <div className="flex gap-2">
                    <Tag color="blue">{images.length} photos</Tag>
                    <Tag color="gold">Cover image first</Tag>
                </div>
            </div>
            <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800">
                <Image
                    src={activeImage}
                    alt={title}
                    className="aspect-video w-full object-cover"
                    fallback="/placeholder.jpg"
                    preview={{mask: "Preview"}}
                />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
                {images.map((image, index) => (
                    <div
                        key={`${image}-${index}`}
                        className={`overflow-hidden rounded-xl border transition ${
                            activeImage === image
                                ? "border-cyan-400 shadow-sm"
                                : "border-gray-200 dark:border-gray-700"
                        }`}
                    >
                        <button
                            type="button"
                            onClick={() => setActiveImage(image)}
                            className="relative block w-full"
                        >
                            <img
                                src={image}
                                alt={`${title} image ${index + 1}`}
                                className="h-28 w-full object-cover"
                            />
                            {index === 0 ? (
                                <div className="absolute left-2 top-2 rounded bg-black/75 px-2 py-1 text-xs text-white">
                                    <StarFilled className="mr-1"/>
                                    Cover
                                </div>
                            ) : null}
                        </button>
                        <div className="grid grid-cols-2 gap-2 p-2">
                            <Button size="small" onClick={() => setCoverImage(index)} disabled={index === 0}>
                                Set Cover
                            </Button>
                            <Button size="small" danger icon={<DeleteOutlined/>} onClick={() => removeImage(index)}>
                                Delete
                            </Button>
                            <Button size="small" icon={<LeftOutlined/>} onClick={() => moveImage(index, -1)} disabled={index === 0}>
                                Left
                            </Button>
                            <Button
                                size="small"
                                icon={<RightOutlined/>}
                                onClick={() => moveImage(index, 1)}
                                disabled={index === images.length - 1}
                            >
                                Right
                            </Button>
                        </div>
                    </div>
                ))}
                <label className="flex min-h-40 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 p-4 text-center transition hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-zinc-900">
                    <UploadOutlined style={{fontSize: 24}}/>
                    <span className="mt-2">Upload images</span>
                    <span className="text-xs text-zinc-500">JPG, PNG, WEBP</span>
                    <input
                        type="file"
                        multiple
                        hidden
                        accept="image/*"
                        onChange={(event) => {
                            addImages(event.target.files);
                            event.currentTarget.value = "";
                        }}
                    />
                </label>
            </div>
        </div>
    );
}
